/**
 * useMembersData Hook
 * 
 * Custom hook for fetching and managing members data with pagination.
 */

import * as React from "react";
import { userService, type Member, type MemberListResponse } from "@/services/userService";

// Fixed page size for user list
export const FIXED_PAGE_SIZE = 20;

export interface UseMembersDataOptions {
  updateUrlWithCursor: (cursor: string | null, direction: "next" | "previous") => void;
  /** Admin provinsi (userStage 2): dipakai jika URL/request tidak menyertakan province */
  lockedProvince?: string;
  /** Admin kota/kab (userStage 1): dipakai jika URL/request tidak menyertakan city */
  lockedCity?: string;
}

export function useMembersData(options: UseMembersDataOptions) {
  const { updateUrlWithCursor, lockedProvince, lockedCity } = options;

  const lockedProvinceRef = React.useRef(lockedProvince);
  React.useEffect(() => {
    lockedProvinceRef.current = lockedProvince;
  }, [lockedProvince]);

  const lockedCityRef = React.useRef(lockedCity);
  React.useEffect(() => {
    lockedCityRef.current = lockedCity;
  }, [lockedCity]);

  const [members, setMembers] = React.useState<Member[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  
  // Pagination
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [previousCursor, setPreviousCursor] = React.useState<string | null>(null);
  const [hasNext, setHasNext] = React.useState(false);
  const [hasPrevious, setHasPrevious] = React.useState(false);
  const [currentDirection, setCurrentDirection] = React.useState<"next" | "previous">("next");
  const [currentCursor, setCurrentCursor] = React.useState<string | null>(null);

  // Fetch members function
  const fetchMembers = React.useCallback(async (
    direction: "next" | "previous" = "next",
    cursor?: string | null,
    search?: string,
    province?: string,
    city?: string,
    startDate?: Date,
    endDate?: Date,
    dateField?: "tanggal_terbit" | "masa_laku" | "masa_laku_kta",
    tipeIar?: string,
    sortBy?: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id",
    sortOrder?: "asc" | "desc"
  ) => {
    setLoading(true);
    setError(null);

    try {
      const resolvedProvince =
        (province && province.trim()) || lockedProvinceRef.current?.trim() || undefined;

      const resolvedCity =
        (city && city.trim()) || lockedCityRef.current?.trim() || undefined;

      const filterParams = {
        cursor: cursor || undefined,
        limit: FIXED_PAGE_SIZE,
        direction,
        search: search || undefined,
        province: resolvedProvince,
        city: resolvedCity,
        startDate: startDate ? startDate.toISOString().split("T")[0] : undefined,
        endDate: endDate ? endDate.toISOString().split("T")[0] : undefined,
        dateField: dateField || undefined,
        tipe_iar: tipeIar || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined,
        flat: true,
      };

      const result = await userService.fetchMembers(filterParams);

      setMembers(result.data);
      setNextCursor(result.nextCursor);
      setPreviousCursor(result.previousCursor);
      setHasNext(result.hasNext);
      setHasPrevious(result.hasPrevious);
      setCurrentDirection(direction);
      
      // Update cursor
      const newCursor = result.nextCursor || result.previousCursor || null;
      setCurrentCursor(newCursor);
      
      // Update URL with cursor while preserving filters
      if (newCursor) {
        updateUrlWithCursor(newCursor, direction);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      setError(err instanceof Error ? err.message : "Gagal memuat data anggota");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [updateUrlWithCursor]);

  // Initial load (ulang saat lockedProvince terisi — mis. URL hanya ?cursor=… tanpa province)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("search") || "";
    const provinceParam = urlParams.get("province") || undefined;
    const cityParam = urlParams.get("city") || undefined;
    const startDateParam = urlParams.get("startDate");
    const endDateParam = urlParams.get("endDate");
    const dateFieldParam = urlParams.get("dateField") as "tanggal_terbit" | "masa_laku" | "masa_laku_kta" | null;
    const tipeIarParam = urlParams.get("tipe_iar") || undefined;
    const cursorParam = urlParams.get("cursor") || undefined;
    const directionParam = (urlParams.get("direction") || "next") as "next" | "previous";
    const sortByParam = urlParams.get("sortBy") as "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id" | null;
    const sortOrderParam = urlParams.get("sortOrder") as "asc" | "desc" | null;
    
    const startDate = startDateParam ? new Date(startDateParam) : undefined;
    const endDate = endDateParam ? new Date(endDateParam) : undefined;
    const dateField = dateFieldParam && ["tanggal_terbit", "masa_laku", "masa_laku_kta"].includes(dateFieldParam) ? dateFieldParam : "masa_laku";

    const effectiveProvince =
      provinceParam?.trim() || lockedProvinceRef.current?.trim() || undefined;

    const effectiveCity =
      cityParam?.trim() || lockedCityRef.current?.trim() || undefined;

    fetchMembers(
      directionParam,
      cursorParam || null,
      searchParam,
      effectiveProvince,
      effectiveCity,
      startDate,
      endDate,
      dateField,
      tipeIarParam,
      sortByParam || undefined,
      sortOrderParam || undefined
    );
  }, [fetchMembers, lockedProvince, lockedCity]);

  return {
    members,
    loading,
    error,
    nextCursor,
    previousCursor,
    hasNext,
    hasPrevious,
    currentDirection,
    currentCursor,
    fetchMembers,
    setCurrentCursor,
  };
}

